import React, { useCallback, useEffect, useRef, useState } from "react";
import { Activity, CheckCircle, XCircle, HelpCircle, RefreshCw, Clock } from "lucide-react";
import { getSystemHealth, type SystemHealthResponse } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

const SERVICE_LABELS: Record<string, string> = {
  "api-gateway": "API Gateway",
  "auth-service": "Auth Service",
  "config-service": "Config Service",
  "analytics-service": "Analytics Service",
  "integration-service": "Integration Service",
  "crm-service": "CRM Service",
  "voice-service": "Voice Service",
  "messaging-service": "Messaging Service",
  "ai-orchestrator": "AI Orchestrator",
};

const POLL_MS = 30_000;

export function SystemHealth() {
  usePageTitle("System health");
  // data: last successful response — preserved across failures so cards stay visible.
  const [data, setData] = useState<SystemHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    getSystemHealth()
      .then((d) => { setData(d); setFetchFailed(false); })
      .catch(() => setFetchFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    timerRef.current = setInterval(refresh, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [refresh]);

  const entries = data ? Object.entries(data.services) : [];
  const checkedAt = data?.checkedAt ? new Date(data.checkedAt).toLocaleTimeString() : null;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>System health</h1>
          <p className="muted">Live status of all Orbit services. Auto-refreshes every 30 s.</p>
        </div>
        <div className="page-actions">
          <button className="btn secondary" onClick={refresh} disabled={loading}>
            <RefreshCw size={14} style={loading ? { animation: "spin 1s linear infinite" } : undefined} />
            Refresh
          </button>
        </div>
      </header>

      {data?.deploying && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 16, borderLeft: "3px solid var(--warn)", display: "flex", alignItems: "center", gap: 10 }}>
          <RefreshCw size={14} style={{ color: "var(--warn)", flexShrink: 0 }} />
          <span>Deployment in progress — services may restart temporarily.</span>
          {data.runUrl && (
            <a href={data.runUrl} target="_blank" rel="noreferrer" style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
              View run →
            </a>
          )}
        </div>
      )}

      {fetchFailed && (
        <div className="card" style={{ padding: "12px 16px", marginBottom: 16, borderLeft: "3px solid var(--danger)", display: "flex", alignItems: "center", gap: 10 }}>
          <XCircle size={14} style={{ color: "var(--danger)", flexShrink: 0 }} />
          <span>Could not reach the health endpoint — service statuses are unknown.</span>
        </div>
      )}

      {loading && !data && (
        <div className="muted">Checking services…</div>
      )}

      <div className="grid-4">
        {entries.map(([key, svc]) => {
          const unknown = fetchFailed;
          return (
            <div key={key} className="card kpi" style={{ gap: 8 }}>
              <div
                className="kpi-icon"
                style={unknown
                  ? { background: "var(--surface-2, #f3f4f6)", color: "var(--muted)" }
                  : svc.ok
                    ? { background: "var(--success-soft)", color: "var(--success)" }
                    : { background: "var(--danger-soft, #fee2e2)", color: "var(--danger)" }}
              >
                <Activity size={18} />
              </div>
              <h3 style={{ marginBottom: 2 }}>{SERVICE_LABELS[key] ?? key}</h3>
              <div className="value" style={{ fontSize: 18 }}>
                {unknown ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--muted)" }}>
                    <HelpCircle size={16} /> Unknown
                  </span>
                ) : svc.ok ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--success)" }}>
                    <CheckCircle size={16} /> Healthy
                  </span>
                ) : (
                  <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--danger)" }}>
                    <XCircle size={16} /> Down
                  </span>
                )}
              </div>
              <div className="delta" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {!unknown && (
                  <>
                    <Clock size={11} />
                    <span className="tnum">{svc.latencyMs} ms</span>
                    {svc.error && <span style={{ marginLeft: 4 }}>· {svc.error}</span>}
                  </>
                )}
              </div>
              <div className="faint" style={{ fontSize: 11, marginTop: 2, wordBreak: "break-all" }}>{svc.url}</div>
            </div>
          );
        })}
      </div>

      {checkedAt && !fetchFailed && (
        <p className="muted" style={{ marginTop: 16, fontSize: 12 }}>Last checked at {checkedAt}</p>
      )}
    </div>
  );
}
